import { useEffect, useRef, useState } from 'react';
import { fallbackDialCode, timezoneDialCodes } from '../data/dialCodes';
import { lookupVisitorLocation, visitorTimeZone } from '../utils/visitorLocation';
import { useLanguage } from '../contexts/LanguageContext';
import type { Locale } from '../types/locale';

// The country most associated with each site language
const languageDialCodes: Partial<Record<Locale, string>> = {
  de: '+49',
  fr: '+33',
  pl: '+48',
  ar: '+971',
};

// Location first (time zone), then the language being browsed in, then the house default
function initialGuess(locale: Locale): string {
  const tz = visitorTimeZone();
  if (timezoneDialCodes[tz]) return timezoneDialCodes[tz];
  if (tz.startsWith('America/')) return '+1';
  return languageDialCodes[locale] ?? fallbackDialCode;
}

// Pre-selects the visitor's country code, unless they have already picked one.
// A network location lookup refines the first guess when it answers.
export function useDialCode(): [string, (code: string) => void] {
  const { locale } = useLanguage();
  const [code, setCode] = useState(() => initialGuess(locale));
  const touched = useRef(false);

  // Switching language re-suggests, as long as nothing was chosen by hand
  useEffect(() => {
    if (!touched.current) setCode(initialGuess(locale));
  }, [locale]);

  useEffect(() => {
    let active = true;
    lookupVisitorLocation().then(({ callingCode }) => {
      if (active && callingCode && !touched.current) setCode(callingCode);
    });
    return () => {
      active = false;
    };
  }, []);

  const choose = (next: string) => {
    touched.current = true;
    setCode(next);
  };

  return [code, choose];
}
